import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { connect } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import qs from 'query-string-for-all';
import { SearchResult, SearchResultError } from '@prisjakt/search-types';
import useThrottleFn from 'react-use/lib/useThrottleFn';
import useDebounce from 'react-use/lib/useDebounce';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import useClickAway from 'react-use/lib/useClickAway';
import actions from '../../search/actions/search';
import AdContainer from '../../../components/ads';
import { SuggestionsResponse, getSuggestionsFromOldFormat } from './oldFormat';
import { getSuggestionsFromNewFormat } from './newFormat';
import { Suggestion } from './types';
import { getMapping } from './redirectQueries';
import { useProductCollectionSuggestionsV2Experiment } from '../../../hooks/experiments';

import * as S from './styles';
import BestSuggestionItem from './BestSuggestionItem';
import OriginalSuggestionItem from './OriginalSuggestionItem';
import SuggestionItem from './SuggestionItem';
import ProductSuggestionItem from './ProductSuggestionItem';
import StoreSuggestionItem from './StoreSuggestionItem';
import CategorySuggestionItem from './CategorySuggestionItem';

const NAV_ID_BEST = -1;
const NAV_ID_ORIGINAL = 0;

const adsViewUpdateActionProp = () => ({
  type: 'ADS_VIEW_UPDATE',
  view: `search_suggestions`,
});

type Props = {
  onClose: () => void;
  adsViewUpdateAction: () => void;
  suggestionClick: (type: Suggestion['type'], position: number) => void;
  includePriceOnProductSuggestions: boolean;
  useNewResponseFormat: boolean;
  query: string;
  setQuery: (query: string) => void;
} & RouteComponentProps;

const gotoUrl = async (
  type: Suggestion['type'],
  id: Suggestion['id'],
  queryParam: string,
  meta?: string
) => {
  const params = { q: queryParam, meta };
  const res = await fetch(
    `/_internal/goto/${type}/${id}?${qs.stringify(params)}`
  );

  if (res.status !== 200) {
    throw new Error(`Cannot resolve url for ${type}`);
  }

  return res.text();
};

const Suggestions = ({
  query,
  history,
  suggestionClick,
  onClose,
  includePriceOnProductSuggestions,
  useNewResponseFormat,
  adsViewUpdateAction,
  setQuery,
}: Props) => {
  const { market } = useContext(AppContext);
  const [selected, setSelected] = useState(0);
  const [suggestions, setSuggestions] = useState<ReadonlyArray<Suggestion>>([]);
  const [input, setInput] = useState<HTMLInputElement>();
  const [searchBar, setSearchBar] = useState<Element>();
  const containerRef = useRef<HTMLUListElement>(null);
  // Keep track of the request count to discard old results
  const requestCount = useRef(0);
  const showProductCollectionSuggestions = useProductCollectionSuggestionsV2Experiment();

  // useThrottleFn requires the fn to accept all parameters in args
  useThrottleFn((_1, _2) => adsViewUpdateAction(), 1500, [
    suggestions,
    selected,
  ]);

  useEffectOnce(() => {
    const value = Array.from(
      document.querySelectorAll('[data-suggestions="search"]')
    ).find(
      (el): el is HTMLInputElement =>
        el instanceof HTMLInputElement && el.offsetParent !== null
    );
    if (value) {
      setInput(value);
    }
  });
  useEffectOnce(() => {
    const value = document.querySelector('[data-searchbar="true"]');
    if (value) {
      setSearchBar(value);
    }
  });

  const setCurrentSuggestion = useCallback(
    (suggestion: Suggestion) => {
      const modifiedQuery = `${suggestion.text.trim()} `;
      setQuery(modifiedQuery);

      if (!input) return;
      input.value = modifiedQuery;
      input.dispatchEvent(new CustomEvent('change'));
    },
    [input, setQuery]
  );

  const handleClick = useCallback(
    async (suggestion: Suggestion) => {
      const { push } = history;

      if (suggestion.type === 'original' && query.length < 2) {
        // Don't redirect to the full page result for queries that are too short. It will just fail.
        return;
      }

      // Log a suggestion click if the user clicked on a suggestion (not on the query itself)
      if (suggestion.type !== 'original' && suggestion.type !== 'best') {
        suggestionClick(suggestion.type, suggestion.navId);
      }
      setCurrentSuggestion(suggestion);

      switch (suggestion.type) {
        // Try to resolve correct. If fail, fall through to default
        case 'best': {
          const url = getMapping(market.code, suggestion.text);
          if (url) {
            onClose();
            push(url);
          }
          break;
        }
        case 'product':
        case 'vcategory':
        case 'category':
        case 'brand_category':
        case 'product_collection':
        case 'offer_collection':
        case 'store': {
          try {
            const url = await gotoUrl(
              suggestion.type,
              suggestion.id,
              suggestion.text,
              suggestion.meta
            );
            onClose();
            push(url);
            break;
            // eslint-disable-next-line no-empty
          } catch (e) {}
        }
        // eslint-disable-next-line no-fallthrough
        default: {
          onClose();
          push(`/search?search=${encodeURIComponent(suggestion.text)}`);
        }
      }
    },
    [history, onClose, suggestionClick, query, setCurrentSuggestion, market]
  );
  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      switch (e.keyCode) {
        // Enter
        case 13: {
          const suggestion = suggestions.find(s => s.navId === selected);

          if (suggestion) {
            handleClick(suggestion);
            if (input) input.blur();
            e.stopPropagation();
          } else {
            handleClick({
              text: query,
              type: 'enter',
              navId: 0,
            });
            if (input) input.blur();
            e.stopPropagation();
          }
          break;
        }
        // Right
        case 39: {
          const suggestion = suggestions.find(s => s.navId === selected);

          if (
            suggestion &&
            input &&
            input.selectionStart === input.selectionEnd &&
            input.selectionStart === input.value.length
          ) {
            input.value = `${suggestion.text} `;

            input.dispatchEvent(new CustomEvent('focus'));
          }
          // Make sure search bar updates text
          break;
        }
        // Up/Down
        case 38:
        case 40: {
          e.preventDefault();
          let nextItem;
          const currentIndex = suggestions.findIndex(s => s.navId === selected);
          if (e.keyCode === 40) {
            // ArrowDown
            nextItem = suggestions[(currentIndex + 1) % suggestions.length];
          } else {
            // ArrowUp
            const nextIndex =
              currentIndex - 1 === -1
                ? suggestions.length - 1
                : currentIndex - 1;

            nextItem = suggestions[nextIndex];
          }

          if (nextItem) {
            if (input) input.value = `${nextItem.text} `;

            setSelected(nextItem.navId);
          }
          break;
        }
        default:
          break;
      }
    },
    [handleClick, query, selected, suggestions, input]
  );
  useEffect(() => {
    if (input) {
      input.addEventListener('keydown', handleKeyPress);
    }
    // Remove event listeners on cleanup
    return () => {
      if (input) {
        input.removeEventListener('keydown', handleKeyPress);
      }
    };
  }, [handleKeyPress, input]);

  useClickAway(containerRef, (e: Event) => {
    if (e.target instanceof Node && searchBar?.contains(e.target)) {
      // It's the search bar, we don't want to close
      return;
    }
    onClose();
  });

  useDebounce(
    async () => {
      const { superSearchEndpoint } = market;
      const myRequestCount = requestCount.current + 1;
      requestCount.current = myRequestCount;

      if (!superSearchEndpoint) {
        return;
      }

      // HACK: Super search endpoint has rfe hardcoded at the moment
      // Fix this when refactoring full search area
      const endpoint =
        superSearchEndpoint.replace('/rfe', '') +
        (useNewResponseFormat ? '/v2' : '');

      let res;

      try {
        res = await fetch(
          `${endpoint}/suggestions?q=${query}&market=${
            market.code
          }&searchInProperties=true${
            includePriceOnProductSuggestions ? '&includePrice=1' : ''
          }${showProductCollectionSuggestions ? '&productCollections=1' : ''}`
        );
        if (res.status !== 200) {
          const searchError: SearchResultError = await res.json();
          throw new Error(`Search failed: ${searchError.error}`);
        }
      } catch (err) {
        return;
      }

      if (myRequestCount !== requestCount.current) {
        // The response we're processing is not from the latest request, discard it
        return;
      }

      let suggestionsFromApi = [];
      if (useNewResponseFormat) {
        const data: SearchResult = await res.json();

        suggestionsFromApi = getSuggestionsFromNewFormat(data);
      } else {
        const data: SuggestionsResponse = await res.json();

        if (data.error) {
          return;
        }

        suggestionsFromApi = getSuggestionsFromOldFormat(data);
      }
      let suggestionsInState: Suggestion[] = [
        { navId: NAV_ID_ORIGINAL, text: query, type: 'original' },
        ...suggestionsFromApi,
      ];
      if (getMapping(market.code, query)) {
        suggestionsInState = [
          { navId: NAV_ID_BEST, text: query, type: 'best' },
          ...suggestionsInState,
        ];
        setSelected(NAV_ID_BEST);
      } else {
        setSelected(NAV_ID_ORIGINAL);
      }

      setSuggestions(suggestionsInState);
    },
    100,
    [
      query,
      includePriceOnProductSuggestions,
      useNewResponseFormat,
      market,
      showProductCollectionSuggestions,
    ]
  );

  return (
    <>
      <S.Wrapper>
        <S.Inner>
          <S.Suggestions ref={containerRef}>
            {suggestions && suggestions.length >= 7 && (
              <S.AdItem style={{ order: 7 }}>
                <AdContainer
                  key="dubbelpanorama1"
                  className="with-position-1"
                  placement="dubbelpanorama1"
                  format="panorama_1"
                  min={0}
                  max={860}
                  async
                />
              </S.AdItem>
            )}
            {suggestions &&
              suggestions.map((suggestion, i) => {
                switch (suggestion.type) {
                  case 'best': {
                    return (
                      <BestSuggestionItem
                        key="best"
                        onClick={() => handleClick(suggestion)}
                        query={query}
                        selected={selected === NAV_ID_BEST}
                      />
                    );
                  }
                  case 'original': {
                    return (
                      <OriginalSuggestionItem
                        key="original"
                        selected={selected === NAV_ID_ORIGINAL}
                        query={query}
                        onClick={() => handleClick(suggestion)}
                      />
                    );
                  }
                  case 'suggestion': {
                    return (
                      <SuggestionItem
                        key={suggestion.text}
                        index={i}
                        text={suggestion.text}
                        selected={suggestion.navId === selected}
                        onSuggestionButtonClick={() => {
                          setCurrentSuggestion(suggestion);
                          if (input) input.focus();
                        }}
                        onClick={() => {
                          handleClick(suggestion);
                        }}
                      />
                    );
                  }
                  case 'product': {
                    return (
                      <ProductSuggestionItem
                        key={suggestion.text}
                        id={suggestion.id}
                        selected={suggestion.navId === selected}
                        onClick={() => handleClick(suggestion)}
                        text={suggestion.text}
                        index={i}
                        price={suggestion.price}
                        includePriceOnProductSuggestions={
                          includePriceOnProductSuggestions
                        }
                      />
                    );
                  }
                  case 'store': {
                    return (
                      <StoreSuggestionItem
                        key={suggestion.text}
                        selected={suggestion.navId === selected}
                        onClick={() => handleClick(suggestion)}
                        text={suggestion.text}
                        index={i}
                        logo={suggestion.logo}
                      />
                    );
                  }
                  default:
                    if (
                      suggestion.type &&
                      (suggestion.type === 'product_collection' ||
                        suggestion.type === 'offer_collection' ||
                        suggestion.type?.indexOf('category') !== -1)
                    ) {
                      return (
                        <CategorySuggestionItem
                          key={suggestion.text}
                          selected={suggestion.navId === selected}
                          onClick={() => handleClick(suggestion)}
                          text={suggestion.text}
                          index={i}
                          type={suggestion.type}
                          logo={suggestion.logo}
                        />
                      );
                    }
                    break;
                }
                return null;
              })}
          </S.Suggestions>
        </S.Inner>
      </S.Wrapper>
    </>
  );
};

export default connect(undefined, {
  ...actions,
  adsViewUpdateAction: adsViewUpdateActionProp,
})(withRouter(Suggestions));
